/**
 * <lph-address>: who a posted card goes to (post direct, docs/post-direct.md). One component for
 * the product page, the basket's "Change address" and the multipack builder.
 *
 *   <lph-address data-value="Jo, 12 High Street, Bristol, BS1 4DJ" data-name="Jo"></lph-address>
 *
 * - data-value: an existing "Posting to" to edit; data-name: a starting envelope name.
 * - .activate(): call once it's on screen; loads the Google address finder (UK only, key from the
 *   "Little Paper Hub" theme setting, <meta name="lph-places-key">). No key, or it can't load: the
 *   boxes are shown to type in. The finder only speeds things up: every box stays editable.
 * - .value(): "Name, Line 1, Line 2, Town, Postcode" (line 2 left out when empty), or null after
 *   pointing at what's missing. Nothing is validated beyond the boxes being filled (new postcodes
 *   exist), except a missing house number, which is asked about once (named houses are fine).
 * - .addressKey(): the address without the name, normalised (cards to the same house share it).
 * - .filled(): are the name and address boxes filled in? (No messages; for progress displays.)
 * - .error(message, input): show a message in the component (e.g. the caller's own checks).
 * - .nameInput: the envelope name box (the product page fills it from the card's Name answer).
 * - data-no-book: no address book choices or save tick (the address book page itself).
 * - data-no-signin: no "Sign in to use them" (the builder asks once, at the start, instead).
 * - data-same-tab: the page keeps what's been filled in and signs in in this tab (it handles the
 *   cancelable "lph:sign-in" event); without it, sign-in opens in a new tab.
 * - .saveAddress(): false when "Save this address for next time" is unticked; callers then add the
 *   hidden property "_save_address": "no" to the card (the address book skips it).
 * - Address book (docs/post-direct.md): above the name, one-tap choices of people already in this
 *   basket (no sign-in needed) and, when signed in, the customer's saved addresses
 *   (/apps/reminders/contacts). Remembered on this device (assets: snippets/lph-attribution.liquid)
 *   with saved addresses but signed out: "Use your saved addresses: sign in", which opens sign-in in
 *   a new tab so nothing typed here is lost; back on this tab, the addresses appear by themselves
 *   (window event "lph:signed-in"). Other people's addresses only ever show after a proper sign-in.
 * - LphAddress.parse(value): back into { name, line1, line2, town, postcode }. Commas are kept out
 *   of the name, town and postcode, so: first part = name, last two = town and postcode, anything
 *   between = the address. (The emails and thank-you page split it the same way.)
 */

/**
 * Relation words, nicknames and spellings ("Nan", "Gramps", "Mam", "Hubby"…). An envelope name made
 * only of these ("Brother", "my Mum", "Nan & Grandad") could be anyone in a busy household.
 */
const RELATION_WORDS = new Set(`
  mum mummy mam mammy mama mam mom mommy momma ma mother mumma mumsy
  dad daddy dada da pa papa pop pops poppa father pappy
  parents parent folks
  nan nana nanna nanny nannie nanny's nanan nain gran granny grannie grandma grandmama grandmum grandmummy grandmother grammy gramma gma
  grandad granddad grandaddy granddaddy grandpa grandpapa grandfather gramps grampa grampy grandpops grumps taid bampa gdad
  grandparents grandparent grandkids grandchildren grandchild grandson granddaughter grandsons granddaughters
  sister sis sissy sisters brother bro bruv brudda brothers siblings sibling
  son sons daughter daughters kids kiddos children child baby babies little one little ones
  auntie aunty aunt aunts aunties uncle unc uncles
  niece nieces nephew nephews cousin cousins cuz
  godchild godchildren goddaughter godson godmother godfather godparents godmum goddad
  wife wifey husband hubby hubs partner other half fiance fiancé fiancee fiancée girlfriend boyfriend gf bf
  love darling sweetheart babe bae
  friend friends bestie besties bff best mate mates pal pals buddy
  neighbour neighbours neighbor neighbors
  teacher teachers miss sir colleague colleagues boss team
  family fam everyone all
  step stepmum stepmom stepdad stepmother stepfather stepson stepdaughter stepbrother stepsister
  in law in-law in-laws inlaws
`.trim().split(/\s+/));

/** "Nan & Grandad", "my Mum", "Mother-in-law": true when every word is a relation word. */
function relationOnly(name) {
  const words = String(name)
    .toLowerCase()
    .replace(/[’']s\b/g, '')
    .replace(/[^a-zà-ÿ&+\s-]/g, ' ')
    .replace(/-/g, ' ')
    .split(/\s+|&|\+/)
    .filter((w) => w && !['my', 'our', 'to', 'dear', 'dearest', 'the', 'and', 'n', 'x'].includes(w));
  return words.length > 0 && words.every((w) => RELATION_WORDS.has(w));
}

const POSTCODE_SHAPE = /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/;
const MISSING = {
  name: 'Please add the name for the envelope.',
  line1: 'Please add the first line of their address.',
  town: 'Please add their town or city.',
  postcode: 'Please add their postcode.',
};

let count = 0;
let places;
let choices;
let signedInLater = false;

/** The shop's sign-in page, coming back to `path` (its URL may already carry a return_to). */
function signInUrl(path) {
  const url = new URL(document.querySelector('meta[name="lph-login"]')?.content || '/account/login', location.origin);
  url.searchParams.set('return_to', path);
  return url.pathname + url.search;
}

const signedInNow = () => signedInLater || document.querySelector('meta[name="lph-signed-in"]')?.content === '1';
const fetchBook = () =>
  fetch('/apps/reminders/contacts', { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

/**
 * Signed in from another tab ("Use your saved addresses: sign in")? Check when this tab is looked at
 * again, and if so tell every address form (and the Elves list) to show the saved addresses.
 */
function watchForSignIn() {
  if (watchForSignIn.on) return;
  watchForSignIn.on = true;
  const check = async () => {
    if (document.visibilityState !== 'visible' || signedInLater) return;
    const book = await fetchBook();
    if (!book) return;
    signedInLater = true;
    choices = null;
    document.removeEventListener('visibilitychange', check);
    window.removeEventListener('focus', check);
    window.dispatchEvent(new CustomEvent('lph:signed-in'));
  };
  document.addEventListener('visibilitychange', check);
  window.addEventListener('focus', check);
  // The signed-in tab says so straight away when the browser keeps the link between the tabs.
  window.addEventListener('message', (event) => {
    if (event.origin === location.origin && event.data?.type === 'lph:signed-in') check();
  });
}

/** "Tim · Ilford" choices: this basket's posted addresses, then the address book (signed in only). */
function loadChoices() {
  choices ??= (async () => {
    const root = window.Shopify?.routes?.root ?? '/';
    const seen = new Map();
    const add = (value, source) => {
      const key = String(value ?? '').toLowerCase().replace(/[\s,]+/g, '');
      if (value && !seen.has(key)) seen.set(key, { value, source });
    };
    const [cart, book] = await Promise.all([
      fetch(`${root}cart.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      signedInNow() ? fetchBook() : null,
    ]);
    const signedIn = !!book;
    for (const item of cart?.items ?? []) add(item.properties?.['Posting to'], 'basket');
    for (const contact of book?.contacts ?? []) add(contact.value, 'book');
    return { list: [...seen.values()], signedIn };
  })();
  return choices;
}

/** The Google Maps script, loaded once per page. */
function loadPlaces(key) {
  if (window.google?.maps?.importLibrary) return Promise.resolve();
  places ??= new Promise((resolve, reject) => {
    window.__lphPlacesReady = resolve;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&callback=__lphPlacesReady&region=GB&language=en-GB`;
    script.async = true;
    script.onerror = reject;
    document.head.append(script);
  });
  return places;
}

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const plain = (value) => String(value).replace(/,/g, ' ').replace(/\s+/g, ' ').trim();

class LphAddress extends HTMLElement {
  static parse(value) {
    const parts = String(value ?? '').split(', ').filter(Boolean);
    if (parts.length < 2) return { name: parts[0] ?? '', line1: '', line2: '', town: '', postcode: '' };
    const middle = parts.slice(1, -2);
    return {
      name: parts[0],
      line1: middle[0] ?? '',
      line2: middle.slice(1).join(', '),
      town: parts.length > 2 ? parts[parts.length - 2] : '',
      postcode: parts[parts.length - 1],
    };
  }

  /** Tidied when it has the usual shape ("sw1a1aa" -> "SW1A 1AA"); otherwise as typed. */
  static postcode(value) {
    const typed = String(value).trim().toUpperCase();
    const compact = typed.replace(/\s+/g, '');
    return POSTCODE_SHAPE.test(compact) ? `${compact.slice(0, -3)} ${compact.slice(-3)}` : typed;
  }

  connectedCallback() {
    // Builders move rows around: build once.
    if (this.built) return;
    this.built = true;
    const id = `lph-address-${++count}`;
    const start = LphAddress.parse(this.dataset.value);
    if (!start.name) start.name = this.dataset.name ?? '';
    this.classList.add('lph-address');
    this.innerHTML = `
      <div class="lph-address__choices" data-choices hidden>
        <p class="lph-address__label">Send to someone you've sent to before</p>
        <div class="lph-address__chips" data-chips></div>
      </div>
      <p class="lph-address__signin" data-signin hidden>
        <span>You have saved addresses.</span>
        <a href="${escapeHtml(signInUrl('/pages/signed-in'))}" target="_blank" rel="opener" data-signin-link>Sign in to use them</a>
        <span class="lph-address__hint">Opens in a new tab, so you won't lose anything here.</span>
      </p>
      <div class="lph-address__field">
        <label for="${id}-name">Name on the envelope</label>
        <p class="lph-address__hint" id="${id}-name-hint">Their name as it should look on the envelope, e.g. Tom Smith or Mr &amp; Mrs Jones</p>
        <input id="${id}-name" type="text" maxlength="80" autocomplete="off" data-a="name" aria-describedby="${id}-name-hint" value="${escapeHtml(start.name)}">
      </div>
      <div class="lph-address__field" data-lookup hidden>
        <p class="lph-address__label" id="${id}-find">Find their address</p>
        <p class="lph-address__hint" id="${id}-find-hint">Start with their house number and street</p>
        <div class="lph-address__find" data-find></div>
        <button type="button" class="lph-address__manual" data-manual>or type it in yourself</button>
      </div>
      <div class="lph-address__fields" data-fields>
        <div class="lph-address__field">
          <label for="${id}-line1">Address</label>
          <input id="${id}-line1" type="text" maxlength="100" autocomplete="off" data-a="line1" value="${escapeHtml(start.line1)}">
          <input type="text" maxlength="100" autocomplete="off" data-a="line2" aria-label="Address line 2 (optional)" placeholder="Line 2 (optional)" value="${escapeHtml(start.line2)}">
        </div>
        <div class="lph-address__row">
          <div class="lph-address__field">
            <label for="${id}-town">Town or city</label>
            <input id="${id}-town" type="text" maxlength="60" autocomplete="off" data-a="town" value="${escapeHtml(start.town)}">
          </div>
          <div class="lph-address__field">
            <label for="${id}-postcode">Postcode</label>
            <input id="${id}-postcode" type="text" maxlength="10" autocomplete="off" autocapitalize="characters" data-a="postcode" value="${escapeHtml(start.postcode)}">
          </div>
        </div>
      </div>
      ${'noBook' in this.dataset ? '' : '<label class="lph-address__save"><input type="checkbox" data-save-address checked> Save this address for next time</label>'}
      <p class="lph-address__error" data-error role="alert" hidden></p>`;
    this.nameInput = this.get('name');
    this.querySelector('[data-manual]').addEventListener('click', () => this.showFields(true));
    this.get('postcode').addEventListener('blur', (e) => (e.target.value = LphAddress.postcode(e.target.value)));
    // A choice picked, then edited: no longer that choice.
    this.querySelector('[data-fields]').addEventListener('input', () => this.markChoice(null));
    this.nameInput.addEventListener('input', () => this.markChoice(null));
    if (this.activateWhenBuilt) this.activate();
  }

  saveAddress() {
    return this.querySelector('[data-save-address]')?.checked !== false;
  }

  /** The address book choices (see the top of the file). */
  async showChoices() {
    // The address book page edits entries: no choices or save tick there (data-no-book).
    if ('noBook' in this.dataset) return;
    const { signedIn } = await this.refreshChoices();
        const parts = LphAddress.parse(value);
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'lph-address__chip';
        chip.dataset.value = value;
        chip.textContent = parts.town ? `${parts.name} · ${parts.town}` : parts.name;
    // Remembered on this device, with saved addresses, but signed out: offer them behind a sign-in.
    const signin = this.querySelector('[data-signin]');
    const offer = () => (signin.hidden = signedIn || 'noSignin' in this.dataset || !window.lphKnownCard?.has_saved_addresses);
    offer();
    if (!signedIn) {
      if (!window.lphKnownCard) window.addEventListener('lph:known', offer, { once: true });
      // The page can keep the card being made and sign in in this tab: it handles "lph:sign-in"
      // (calls preventDefault). Otherwise sign-in opens in a new tab and this tab picks it up.
      const link = signin.querySelector('[data-signin-link]');
      link.onclick = (event) => {
        const here = location.pathname + location.search;
        const sameTab = new CustomEvent('lph:sign-in', { bubbles: true, cancelable: true, detail: { url: signInUrl(here) } });
        if (!this.dispatchEvent(sameTab)) {
          event.preventDefault();
          location.href = signInUrl(here);
          return;
        }
        watchForSignIn();
      };
      if ('sameTab' in this.dataset) {
        link.removeAttribute('target');
        signin.querySelector('.lph-address__hint').textContent = "You'll come straight back here with everything as you left it.";
      }
      if (!this.onSignedIn) {
        this.onSignedIn = () => this.showChoices();
        window.addEventListener('lph:signed-in', this.onSignedIn, { once: true });
      }
    }
  }

  /**
   * The choices again: the basket and saved addresses, plus anyone already filled in on another card
   * on this page (the builder calls this as each card is shown, so Grandma's house can be picked for
   * Mum's card too).
   */
  async refreshChoices() {
    const loaded = await loadChoices();
    if ('noBook' in this.dataset || !this.built) return loaded;
    const seen = new Set();
    const list = [];
    const add = (value) => {
      const key = String(value ?? '').toLowerCase().replace(/[\s,]+/g, '');
      if (value && !seen.has(key)) seen.add(key) && list.push(value);
    };
    const mine = this.filled() ? this.currentValue() : '';
    for (const other of document.querySelectorAll('lph-address')) if (other !== this && other.filled?.()) add(other.currentValue());
    for (const { value } of loaded.list) add(value);
    const chips = this.querySelector('[data-chips]');
    chips.replaceChildren(
      ...list.map((value) => {
        const parts = LphAddress.parse(value);
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'lph-address__chip';
        chip.dataset.value = value;
        chip.textContent = parts.town ? `${parts.name} · ${parts.town}` : parts.name;
        chip.addEventListener('click', () => this.choose(value));
        return chip;
      }),
    );
    this.markChoice(mine);
    this.querySelector('[data-choices]').hidden = !list.length;
    return loaded;
  }

  /** What's in the boxes, in the "Posting to" format, without checking it (for choices elsewhere). */
  currentValue() {
    const get = (k) => this.get(k).value.trim();
    return [plain(get('name')), get('line1'), get('line2'), plain(get('town')), LphAddress.postcode(get('postcode'))].filter(Boolean).join(', ');
  }

  /** Fill the boxes from a choice ("Name, Line 1, …"); everything stays editable. */
  choose(value) {
    const parts = LphAddress.parse(value);
    for (const key of ['name', 'line1', 'line2', 'town', 'postcode']) this.get(key).value = parts[key] ?? '';
    this.nameInput.dataset.edited = '1';
    this.showFields();
    this.error('');
    this.noNumberOk = `${parts.line1} ${parts.line2}`;
    // Callers (pricing, progress) listen for input.
    this.get('line1').dispatchEvent(new Event('input', { bubbles: true }));
    this.markChoice(value);
  }

  markChoice(value) {
    for (const chip of this.querySelectorAll('.lph-address__chip')) chip.setAttribute('aria-pressed', String(chip.dataset.value === value));
  }

  get(key) {
    return this.querySelector(`[data-a="${key}"]`);
  }

  /** Load the address finder (once). An address already in the boxes keeps them showing. */
  activate() {
    // Builders may ask before the element is on the page: start once it's built.
    if (!this.built) {
      this.activateWhenBuilt = true;
      return;
    }
    if (this.started) return;
    this.started = true;
    this.showChoices();
    const key = document.querySelector('meta[name="lph-places-key"]')?.content;
    if (!key) return this.showFields();
    if (!this.get('line1').value) this.querySelector('[data-fields]').hidden = true;
    const find = this.querySelector('[data-find]');
    find.textContent = 'Loading the address finder…';
    this.querySelector('[data-lookup]').hidden = false;
    loadPlaces(key)
      .then(() => this.setupFinder())
      .catch(() => {
        this.querySelector('[data-lookup]').hidden = true;
        this.showFields();
      });
  }

  showFields(focus) {
    this.querySelector('[data-fields]').hidden = false;
    this.querySelector('[data-manual]').hidden = true;
    if (focus) this.get('line1').focus();
  }

  /**
   * Our own box and suggestion list, fed by Google's address suggestions. (Google's ready-made
   * finder opens a full-screen overlay on phones that jumps about when the keyboard opens.)
   */
  async setupFinder() {
    const { AutocompleteSessionToken, AutocompleteSuggestion } = await google.maps.importLibrary('places');
    const find = this.querySelector('[data-find]');
    const listId = `${this.querySelector('.lph-address__label').id}-list`;
    find.innerHTML = `
      <input type="text" autocomplete="off" enterkeyhint="search" placeholder="e.g. 12 High Street" data-finder
        role="combobox" aria-expanded="false" aria-autocomplete="list" aria-controls="${listId}"
        aria-labelledby="${this.querySelector('.lph-address__label').id}" aria-describedby="${this.querySelector('.lph-address__hint').id}">
      <div class="lph-address__suggestions" data-suggestions hidden>
        <ul id="${listId}" role="listbox"></ul>
        <p class="lph-address__google">powered by Google</p>
      </div>`;
    const input = find.querySelector('[data-finder]');
    const box = find.querySelector('[data-suggestions]');
    const list = box.querySelector('ul');
    this.querySelector('[data-lookup]').hidden = false;
    if (this.get('line1').value) this.querySelector('[data-manual]').hidden = true;

    let token = new AutocompleteSessionToken();
    let timer;
    let asked = 0;
    let active = -1;
    const close = () => {
      box.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      active = -1;
    };
    const highlight = (i) => {
      const items = [...list.children];
      active = items.length ? (i + items.length) % items.length : -1;
      items.forEach((li, n) => li.setAttribute('aria-selected', String(n === active)));
      if (active >= 0) input.setAttribute('aria-activedescendant', items[active].id);
    };
    const lookup = async () => {
      const text = input.value.trim();
      const mine = ++asked;
      if (text.length < 3) return close();
      try {
        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: text,
          includedRegionCodes: ['gb'],
          language: 'en-GB',
          region: 'gb',
          sessionToken: token,
        });
        if (mine !== asked) return;
        const found = suggestions.map((s) => s.placePrediction).filter(Boolean).slice(0, 5);
        list.replaceChildren(
          ...found.map((prediction, n) => {
            const li = document.createElement('li');
            li.id = `${listId}-${n}`;
            li.setAttribute('role', 'option');
            const main = document.createElement('strong');
            main.textContent = prediction.mainText?.text ?? prediction.text.text;
            const rest = document.createElement('span');
            rest.textContent = prediction.secondaryText?.text ?? '';
            li.append(main, rest);
            // pointerdown, not click: picks before the box loses focus and the list closes.
            li.addEventListener('pointerdown', (e) => {
              e.preventDefault();
              this.pick(prediction, input, close);
            });
            return li;
          }),
        );
        box.hidden = !found.length;
        input.setAttribute('aria-expanded', String(found.length > 0));
        active = -1;
      } catch {
        close();
      }
    };
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(lookup, 200);
    });
    input.addEventListener('keydown', (e) => {
      if (box.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        highlight(active + (e.key === 'ArrowDown' ? 1 : -1));
      } else if (e.key === 'Enter' && active >= 0) {
        e.preventDefault();
        list.children[active].dispatchEvent(new Event('pointerdown'));
      } else if (e.key === 'Escape') {
        close();
      }
    });
    input.addEventListener('blur', () => setTimeout(close, 150));
    this.newSession = () => (token = new AutocompleteSessionToken());
  }

  async pick(prediction, input, close) {
    close();
    input.value = prediction.text.text;
    const place = prediction.toPlace();
    await place.fetchFields({ fields: ['addressComponents'] });
    this.newSession();
    const part = (type) => place.addressComponents?.find((c) => c.types.includes(type))?.longText ?? '';
    const street = [part('street_number'), part('route')].filter(Boolean).join(' ');
    const building = [part('subpremise'), part('premise')].filter(Boolean).join(' ');
    this.get('line1').value = building || street;
    this.get('line2').value = building ? street : part('sublocality') || '';
    this.get('town').value = part('postal_town') || part('locality');
    this.get('postcode').value = LphAddress.postcode(part('postal_code'));
    this.showFields();
    this.error('');
    // Picking just a street gives no house number: ask for it, cursor ready before the street.
    if (!part('street_number') && !building && street) {
      this.error('Please add their house number (or name) to the address.', this.get('line1'));
      this.get('line1').setSelectionRange(0, 0);
      return;
    }
    const empty = ['line1', 'town', 'postcode'].map((k) => this.get(k)).find((i) => !i.value);
    (empty ?? this.get('line1')).focus();
  }

  /** Where this card is going, for "same address" pricing: everything but the name, normalised. */
  addressKey() {
    if (!this.built) return '';
    return ['line1', 'line2', 'town', 'postcode'].map((k) => this.get(k).value).join(',').toLowerCase().replace(/[\s,]+/g, '');
  }

  filled() {
    if (!this.built) return false;
    return ['name', 'line1', 'town', 'postcode'].every((k) => this.get(k).value.trim());
  }

  error(message, input) {
    const box = this.querySelector('[data-error]');
    box.textContent = message;
    box.hidden = !message;
    if (input) {
      input.focus();
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  value() {
    const get = (k) => this.get(k);
    if (this.querySelector('[data-fields]').hidden) {
      if (!get('name').value.trim()) {
        this.error(MISSING.name, get('name'));
        return null;
      }
      this.error('Please find their address above, or type it in yourself.', this.querySelector('[data-finder]') ?? this.querySelector('[data-manual]'));
      return null;
    }
    for (const k of ['name', 'line1', 'town', 'postcode']) {
      if (!get(k).value.trim()) {
        this.error(MISSING[k], get(k));
        return null;
      }
    }
    get('postcode').value = LphAddress.postcode(get('postcode').value);
    // Just a relation as the envelope name: ask once; the same name again goes through.
    const envelope = get('name').value.trim();
    if (relationOnly(envelope) && this.relationNameOk !== envelope) {
      this.relationNameOk = envelope;
      this.error(`“${envelope}” on its own could confuse a busy household. Please add their name (e.g. Tom Smith), or press the button again to keep it.`, get('name'));
      return null;
    }
    // No number anywhere: ask once; the same address again goes through (some houses only have a name).
    const lines = `${get('line1').value} ${get('line2').value}`;
    if (!/\d/.test(lines) && this.noNumberOk !== lines) {
      this.noNumberOk = lines;
      this.error("There's no house number. Please add it, or if the house just has a name, press the button again.", get('line1'));
      return null;
    }
    this.error('');
    return [plain(get('name').value), get('line1').value.trim(), get('line2').value.trim(), plain(get('town').value), plain(get('postcode').value)]
      .filter(Boolean)
      .join(', ');
  }
}

if (!customElements.get('lph-address')) customElements.define('lph-address', LphAddress);
window.LphAddress = LphAddress;
